import clsx from "clsx";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { gasToken, networkLabel } from "rujira.js";
import {
  Button,
  Icons,
  SwapSelect,
  SwapSelectEntry,
  SwapSelectOption,
  Warning,
  useTranslation,
} from "rujira.ui";
import { useWalletAccounts } from "../../services/accounts";
import {
  BanxaFiat,
  BanxaFiatPaymentMethod,
  BanxaQuote,
  BanxaSellCapabilities,
  banxaFeeTotal,
  createBanxaSellOrder,
  getBanxaSellCapabilities,
  getBanxaSellQuote,
} from "../../services/banxa";
import { banxaNeedsGas, banxaSellMax } from "../../services/banxaGasReserve";
import {
  AMOUNT_DECIMALS,
  banxaGasBalance,
  getBanxaSellSources,
} from "../../services/banxaSellSources";
import { usePreloadedBalances } from "./Balance";
import { BanxaFeeBreakdown } from "./BanxaFeeBreakdown";
import { BanxaReviewRow } from "./BanxaReviewRow";

const AMOUNT_SCALE = 10n ** BigInt(AMOUNT_DECIMALS);

const capabilitiesContext = createContext<BanxaSellCapabilities | undefined>(
  undefined
);

export const BanxaSellCapabilitiesProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [capabilities, setCapabilities] = useState<BanxaSellCapabilities>();

  useEffect(() => {
    if (!__BANXA_ENABLED__) return;

    let active = true;
    getBanxaSellCapabilities()
      .then((result) => {
        if (active) setCapabilities(result);
      })
      .catch(() => {
        // This is an optional action. Leave it out when Banxa is unavailable.
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <capabilitiesContext.Provider value={capabilities}>
      {children}
    </capabilitiesContext.Provider>
  );
};

export const useBanxaSellCapabilities = () => useContext(capabilitiesContext);

const formatAmount = (amount: bigint) => {
  const whole = amount / AMOUNT_SCALE;
  const fraction = (amount % AMOUNT_SCALE)
    .toString()
    .padStart(AMOUNT_DECIMALS, "0")
    .replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
};

const parseAmount = (input?: string) => {
  if (!input || !/^\d+(?:\.\d+)?$/.test(input)) return 0n;
  const [whole, fraction = ""] = input.split(".");
  return (
    BigInt(whole) * AMOUNT_SCALE +
    BigInt(fraction.slice(0, AMOUNT_DECIMALS).padEnd(AMOUNT_DECIMALS, "0"))
  );
};

const formatNumber = (value: string, maximumFractionDigits = 8) => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(parsed)
    : value;
};

const fiatOption = (fiat: BanxaFiat): SwapSelectOption => ({
  asset: `fiat:${fiat.id}`,
  type: "FIAT",
  chain: "ETH",
  metadata: { symbol: fiat.id, decimals: AMOUNT_DECIMALS },
});

const paymentMethodName = (method?: BanxaFiatPaymentMethod) =>
  method?.name ||
  method?.id
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") ||
  "";

const shortAddress = (address: string) =>
  `${address.slice(0, 8)}…${address.slice(-6)}`;

const quoteRate = (quote?: BanxaQuote) => {
  const fiat = Number(quote?.fiatAmount);
  const crypto = Number(quote?.cryptoAmount);
  return Number.isFinite(fiat) && Number.isFinite(crypto) && crypto > 0
    ? fiat / crypto
    : undefined;
};

export const BanxaOffRamp: FC<{
  capabilities: BanxaSellCapabilities;
  initialSourceId: string;
  onBack: () => void;
}> = ({ capabilities, initialSourceId, onBack }) => {
  const { t } = useTranslation("common");
  const accountProvider = useWalletAccounts();
  const balances = usePreloadedBalances(false);
  const sources = useMemo(
    () => getBanxaSellSources(balances, capabilities),
    [balances, capabilities]
  );
  const [stage, setStage] = useState<"configure" | "review">("configure");
  const [sourceId, setSourceId] = useState(initialSourceId);
  const [cryptoAmount, setCryptoAmount] = useState(0n);
  const [fiatId, setFiatId] = useState(
    capabilities.fiats.find(({ id }) => id === "USD")?.id ||
      capabilities.fiats[0]?.id ||
      ""
  );
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [quote, setQuote] = useState<BanxaQuote>();
  const [quoteState, setQuoteState] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const source = sources.find(({ id }) => id === sourceId) || sources[0];
  const fiat =
    capabilities.fiats.find(({ id }) => id === fiatId) || capabilities.fiats[0];
  const paymentMethods = fiat?.supportedPaymentMethods || [];
  const paymentMethod = paymentMethods.find(({ id }) => id === paymentMethodId);

  useEffect(() => {
    if (source && source.id !== sourceId) setSourceId(source.id);
  }, [source, sourceId]);

  useEffect(() => {
    if (!paymentMethods.some(({ id }) => id === paymentMethodId)) {
      setPaymentMethodId(paymentMethods[0]?.id || "");
    }
  }, [paymentMethodId, paymentMethods]);

  // The disclaimer is agreed against one specific order. Changing any part of it
  // means the user has not agreed to what they would now be submitting.
  useEffect(() => {
    setAccepted(false);
  }, [sourceId, cryptoAmount, fiatId, paymentMethodId]);

  const gasBalance = source
    ? banxaGasBalance(
        balances,
        source.network,
        source.account.address,
        gasToken(source.network).symbol
      )
    : 0n;
  const missingGas =
    !!source &&
    banxaNeedsGas(
      source.network,
      source.account.asset.metadata.symbol,
      gasBalance
    );
  const sellMax = source
    ? banxaSellMax(
        source.network,
        source.account.asset.metadata.symbol,
        source.account.balance,
        gasBalance
      )
    : 0n;

  useEffect(() => {
    if (source && cryptoAmount > sellMax) setCryptoAmount(sellMax);
  }, [cryptoAmount, sellMax, source]);

  const minimumCrypto = parseAmount(source?.minimum);
  const belowCryptoMinimum =
    cryptoAmount > 0n && minimumCrypto > 0n && cryptoAmount < minimumCrypto;
  const cryptoAmountValue = formatAmount(cryptoAmount);

  useEffect(() => {
    if (
      !source ||
      !fiat ||
      !paymentMethodId ||
      cryptoAmount === 0n ||
      belowCryptoMinimum
    ) {
      setQuote(undefined);
      setQuoteState("idle");
      setError("");
      return;
    }

    let active = true;
    setQuote(undefined);
    setQuoteState("loading");
    setError("");
    const timeout = window.setTimeout(() => {
      getBanxaSellQuote({
        fiat: fiat.id,
        crypto: source.crypto,
        blockchain: source.blockchain,
        paymentMethodId,
        cryptoAmount: cryptoAmountValue,
      })
        .then((nextQuote) => {
          if (!active) return;
          setQuote(nextQuote);
          setQuoteState("ready");
        })
        .catch(() => {
          if (!active) return;
          setQuoteState("error");
          setError(t("banxaSellQuoteError"));
        });
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [
    belowCryptoMinimum,
    cryptoAmount,
    cryptoAmountValue,
    fiat?.id,
    paymentMethodId,
    source?.blockchain,
    source?.crypto,
  ]);

  if (!source || !fiat) {
    return (
      <div className="banxa-ramp">
        <button
          type="button"
          className="transparent color-white hover-primary1 flex ai-c fs-14 fw-500"
          onClick={onBack}>
          <Icons.AngleLeft className="block w-2 h-2 mr-0.5" />
          {t("back")}
        </button>
        <div className="banxa-ramp__empty mt-2">
          <Icons.Info className="w-3 h-3" />
          <div>
            <strong>{t("banxaNoSellAssetsTitle")}</strong>
            <span>{t("banxaNoSellAssetsBody")}</span>
          </div>
        </div>
      </div>
    );
  }

  const fiatAmount = parseAmount(quote?.fiatAmount);
  const minimumFiat = parseAmount(paymentMethod?.minimum);
  const belowFiatMinimum =
    quoteState === "ready" && minimumFiat > 0n && fiatAmount < minimumFiat;
  const fee = banxaFeeTotal(quote);
  const rate = quoteRate(quote);
  const valueUsd =
    source.account.balance > 0n
      ? (cryptoAmount * source.account.valueUsd) / source.account.balance
      : 0n;
  const sourceOptions = sources.map((item) => ({
    asset: item.option,
    balance: item.account.balance,
    valueUsd: item.account.valueUsd,
    secondaryLabel: networkLabel(item.network),
  }));
  const sourceGroupValues = sourceOptions.reduce((values, option) => {
    const symbol = option.asset.metadata.symbol.toUpperCase();
    values.set(symbol, (values.get(symbol) || 0n) + (option.valueUsd || 0n));
    return values;
  }, new Map<string, bigint>());
  const sortSourceOptions = (
    a: SwapSelectEntry<SwapSelectOption>,
    b: SwapSelectEntry<SwapSelectOption>
  ) => {
    const aSymbol = a.asset.metadata.symbol.toUpperCase();
    const bSymbol = b.asset.metadata.symbol.toUpperCase();
    const aGroupValue = sourceGroupValues.get(aSymbol) || 0n;
    const bGroupValue = sourceGroupValues.get(bSymbol) || 0n;

    if (aGroupValue !== bGroupValue) return aGroupValue > bGroupValue ? -1 : 1;

    return (
      aSymbol.localeCompare(bSymbol) ||
      (a.secondaryLabel || "").localeCompare(b.secondaryLabel || "")
    );
  };
  const fiatOptions = capabilities.fiats.map((item) => ({
    asset: fiatOption(item),
  }));

  const submit = async () => {
    if (!quote || !accepted || belowFiatMinimum) return;
    setSubmitting(true);
    setError("");

    try {
      const order = await createBanxaSellOrder({
        fiat: fiat.id,
        crypto: source.crypto,
        blockchain: source.blockchain,
        paymentMethodId,
        cryptoAmount: cryptoAmountValue,
        walletAddress: source.account.address,
        customerId:
          accountProvider.selected?.address.address || source.account.address,
      });
      window.location.assign(order.checkoutUrl);
    } catch {
      setError(t("banxaSellOrderError"));
      setSubmitting(false);
    }
  };

  return (
    <div className="banxa-ramp">
      <div className="banxa-ramp__header">
        <button
          type="button"
          className="transparent color-white hover-primary1 flex ai-c fs-14 fw-500"
          onClick={stage === "review" ? () => setStage("configure") : onBack}>
          <Icons.AngleLeft className="block w-2 h-2 mr-0.5" />
          {t("back")}
        </button>
        {__BANXA_ENVIRONMENT__ === "sandbox" && (
          <span className="banxa-ramp__environment">{t("sandbox")}</span>
        )}
      </div>

      <h3 className="h3 mb-0">{t("sellCrypto")}</h3>
      <p className="p color-grey">
        {stage === "configure"
          ? t("banxaSellIntro")
          : t("banxaSellReviewIntro")}
      </p>

      {stage === "configure" ? (
        <>
          <span className="banxa-ramp__eyebrow">{t("youPay")}</span>
          <SwapSelect
            selected={source.option}
            options={sourceOptions}
            sortOptions={sortSourceOptions}
            onSelect={(asset) => {
              const next = sources.find(
                ({ option }) => option.asset === asset.asset
              );
              if (next) setSourceId(next.id);
            }}
            amount={cryptoAmount}
            onChangeAmount={setCryptoAmount}
            max={sellMax}
            value={valueUsd}
            className="swap-select--responsive swap-select--dark"
          />
          <p className="banxa-ramp__source-wallet">
            {networkLabel(source.network)} ·{" "}
            {shortAddress(source.account.address)}
          </p>
          {missingGas && source && (
            <Warning
              color="orange"
              msg={t("banxaSellGasMissing", {
                asset: source.account.asset.metadata.symbol,
                gas: gasToken(source.network).symbol,
                network: networkLabel(source.network),
              })}
              className="mt-2 condensed"
            />
          )}
          {belowCryptoMinimum && (
            <p className="banxa-ramp__minimum-error">
              {t("banxaMinimumSale", {
                asset: source.crypto,
                amount: source.minimum,
              })}
            </p>
          )}

          <span className="banxa-ramp__eyebrow mt-2">{t("youReceive")}</span>
          <SwapSelect
            selected={fiatOption(fiat)}
            options={fiatOptions}
            onSelect={(asset) => setFiatId(asset.metadata.symbol)}
            sortOptions={() => 0}
            amount={fiatAmount}
            className="swap-select--responsive swap-select--dark"
          />
          {belowFiatMinimum && (
            <p className="banxa-ramp__minimum-error">
              {t("banxaMinimumPayout", {
                fiat: fiat.id,
                amount: paymentMethod?.minimum,
              })}
            </p>
          )}

          <span className="banxa-ramp__eyebrow mt-2">{t("payoutMethod")}</span>
          <div className="banxa-ramp__payment-methods">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                type="button"
                aria-pressed={paymentMethodId === method.id}
                className={clsx("banxa-ramp__payment-method", {
                  "banxa-ramp__payment-method--selected":
                    paymentMethodId === method.id,
                })}
                onClick={() => setPaymentMethodId(method.id)}>
                <Icons.Building className="w-3.5 h-3.5" />
                <span>
                  <strong>{paymentMethodName(method)}</strong>
                  <small>
                    {t("banxaPayoutRange", {
                      fiat: fiat.id,
                      minimum: method.minimum,
                      maximum: method.maximum,
                    })}
                  </small>
                </span>
              </button>
            ))}
          </div>

          <div className="banxa-ramp__quote" aria-live="polite">
            <div>
              <span>{t("estimatedFees")}</span>
              <strong>
                {quote ? `${formatNumber(String(fee), 2)} ${fiat.id}` : "—"}
              </strong>
            </div>
            <div>
              <span>{t("effectiveRate")}</span>
              <strong>
                {rate
                  ? `1 ${source.crypto} ≈ ${formatNumber(String(rate), 2)} ${fiat.id}`
                  : "—"}
              </strong>
            </div>
          </div>

          {error && <BanxaErrorMessage message={error} />}

          <Button
            className="button--big w-full mt-2"
            disabled={
              !quote ||
              quoteState !== "ready" ||
              belowCryptoMinimum ||
              belowFiatMinimum
            }
            onClick={() => setStage("review")}
            label={
              quoteState === "loading"
                ? t("gettingQuote")
                : t("reviewWithdrawal")
            }
          />
        </>
      ) : (
        <>
          <div className="card px-2 py-1 mt-2">
            <BanxaReviewRow
              label={t("youPay")}
              value={`${formatNumber(quote?.cryptoAmount || cryptoAmountValue)} ${source.crypto}`}
            />
            <BanxaReviewRow
              label={t("youReceive")}
              value={`≈ ${formatNumber(quote?.fiatAmount || "0", 2)} ${fiat.id}`}
            />
            <BanxaFeeBreakdown quote={quote} fiat={fiat.id} />
            <BanxaReviewRow
              label={t("payoutMethod")}
              value={paymentMethodName(paymentMethod)}
            />
            <BanxaReviewRow
              label={t("network")}
              value={networkLabel(source.network)}
            />
            <BanxaReviewRow
              label={t("sendingWallet")}
              value={source.account.address}
              mono
            />
          </div>

          <div className="card p-2 mt-2">
            <label className="banxa-ramp__provider">
              <span className="banxa-ramp__provider-title">
                <span className="banxa-ramp__wordmark" aria-label="Banxa">
                  B<span>△</span>NX<span>△</span>
                </span>
                <span>{t("serviceProvider")}</span>
              </span>
              <span className="banxa-ramp__provider-copy">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(event) => setAccepted(event.currentTarget.checked)}
                />
                <span>{t("banxaSellDisclaimer")}</span>
              </span>
            </label>
          </div>

          <div className="banxa-ramp__gas-note">
            <Icons.Info className="w-2 h-2 color-orange" />
            <p className="m-0">{t("banxaSellTransferNotice")}</p>
          </div>

          <p className="banxa-ramp__indicative">{t("banxaIndicativeQuote")}</p>
          {error && <BanxaErrorMessage message={error} />}

          <div className="modal__footer banxa-ramp__footer px-3 py-2">
            <Button
              className="button--grey button--outline"
              onClick={() => setStage("configure")}
              label={t("back")}
            />
            <Button
              className={clsx("ml-1", { "button--waiting": submitting })}
              disabled={!accepted || submitting}
              onClick={submit}
              label={submitting ? t("openingBanxa") : t("continueWithBanxa")}
            />
          </div>
        </>
      )}
    </div>
  );
};

const BanxaErrorMessage: FC<{ message: string }> = ({ message }) => (
  <div className="banxa-ramp__error" role="alert">
    <Icons.Info aria-hidden="true" />
    <span>{message}</span>
  </div>
);
